import { ChangeEvent, useState } from 'react';

import { DownloadIcon } from '../../../../assets/svg';
import { DragAndDrop } from '../../../../components/drag-and-drop';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { OutboundService } from '../../../../services/outbound';
import { fullDateFormat } from '../../../../utils/dates';
import { errorFormatter } from '../../../../utils/errors';
import {
	Button,
	ButtonContainer,
	DownloadLink,
	DragAndDropContainer,
	List,
	ListItem,
	ListItemStrong,
	ModalContainer,
	ModalStep,
	ModalSubtitle,
	ModalTitle,
} from '../modal/styles';
import OutboundResume from '../outboundResume';
import SendCode from '../sendCode';

interface NewBatchPaymentProps {
	show: boolean;
	onClose: () => void;
}

interface DefaultState {
	loading: boolean;
	files?: FileList | FileList[];
	buttonDisabled: boolean;
	code: string;
	token: string;
	totalAmount: number;
	quantityAmount: number;
	requestDate: string;
	status: string;
}

const defaultState: DefaultState = {
	loading: false,
	files: undefined,
	buttonDisabled: true,
	code: '',
	token: '',
	requestDate: '',
	status: '',
	totalAmount: 0,
	quantityAmount: 0,
};

interface ErrorState {
	quantityPayment: string;
	totalAmount: string;
	csv: string;
}

const errorState: ErrorState = {
	quantityPayment: '',
	totalAmount: '',
	csv: '',
};

const NewBatchPayment = ({ show, onClose }: NewBatchPaymentProps) => {
	const { merchantSelected, urlToDownloadExamplePayouts } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [steps, setSteps] = useState(1);
	const [state, setState] = useState(defaultState);
	const [error, setError] = useState(errorState);

	const handleCloseModal = () => {
		setState(defaultState);
		setSteps(1);
		onClose();
	};

	const handleContinueAction = async () => {
		setState({ ...state, loading: true });
		try {
			if (!state.files) {
				setState({ ...state, loading: false });
				return;
			}

			const data = await OutboundService.sendBatchOutbound({
				merchantCode: merchantSelected?.code as string,
				file: state.files[0],
			});

			if (data.code === 'E_PAYMENTS_NOTFOUND') {
				const newBlob = new Blob([data.csv]);
				const url = window.URL.createObjectURL(newBlob);
				setError({
					quantityPayment: data.resume.totalTransfersWithError,
					totalAmount: data.resume.amount,
					csv: url,
				});
				setSteps(5);
				return;
			}

			const requestDate = new Date();

			setState((prevState) => ({
				...prevState,
				totalAmount: data.resume.amount,
				quantityAmount: data.resume.totalTransfersSuccess,
				requestDate: fullDateFormat(String(requestDate)),
			}));
			if (steps === 3) {
				return setSteps(3);
			}
			setSteps(2);
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
		} finally {
			setState((prevState) => ({ ...prevState, loading: false }));
		}
	};

	const handleGetCode = async () => {
		setState({ ...state, loading: true });
		try {
			const data = await OutboundService.getCodeOutbound({
				merchantCode: merchantSelected?.code as string,
			});
			setState({ ...state, token: data.token, loading: false });
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
		} finally {
			setState((prevState) => ({ ...prevState, loading: false }));
		}
	};

	const handleContinueSendCode = async () => {
		setState({ ...state, loading: true });
		try {
			const response = await OutboundService.sendCodeOutbound({
				merchantCode: merchantSelected?.code as string,
				code: state.code,
				token: state.token,
			});

			setState((state) => ({
				...state,
				loading: false,
				quantityAmount: response.resume.totalTransfers,
				totalAmount: response.resume.amount,
				requestDate: response.resume.requestDate,
				status: response.resume.status,
			}));
			setState({ ...state, loading: false });
			setSteps(4);
		} catch (e: any) {
			if (e.code === 'E_OTP_INVALID' || e.code === 'E_JWT_EXPIRED') {
				setSteps(3);
				setState({ ...state, loading: false });
				handleShowAlert({
					message: errorFormatter(e.code),
					type: 'error',
				});
				return;
			}
			setSteps(3);
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
			setState({ ...state, loading: false });
		}
	};

	const handleChange = (files: FileList | FileList[]) => {
		const hasFiles = files.length > 0;
		setState({ ...state, files, buttonDisabled: !hasFiles });
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setState({ ...state, [name]: value });
	};

	const handleVerifyDisabledButton = () => {
		if (state.files === undefined) return true;
		return false;
	};

	const resumeData = [
		{
			item: 'Número de pagos de salida',
			data: state.quantityAmount,
		},
		{
			item: 'Monto total',
			data: `$${state.totalAmount}`,
		},
	];

	const processData = [
		{
			item: 'Número de pagos de salida',
			data: state.quantityAmount,
		},
		{
			item: 'Monto total',
			data: `$${state.totalAmount}`,
		},
		{
			item: 'Fecha de solicitud',
			data: state.requestDate,
		},
	];

	const errorData = [
		{
			item: 'Número de pagos con error',
			data: error.quantityPayment,
		},
		{
			item: 'Monto total pagos con error',
			data: `$${error.totalAmount}`,
		},
	];

	return (
		<Modal title="Pagos de salida masivos" show={show} type="medium" onClose={handleCloseModal}>
			{state.loading ? (
				<Spinner />
			) : (
				<ModalContainer>
					{(steps === 1 || steps === 2 || steps === 3) && <ModalStep>PASO {steps} DE 3</ModalStep>}
					<ModalTitle>
						{steps === 1 && 'Sube el archivo .csv'}
						{steps === 2 && 'Verifica la información de los pagos'}
						{steps === 3 && 'Autoriza los pagos de salida'}
						{steps === 4 && 'En proceso'}
						{steps === 5 && 'Error en la solicitud'}
					</ModalTitle>
					<ModalSubtitle align={steps === 1 ? 'start' : 'center'}>
						{steps === 1 && (
							<>
								Descarga{' '}
								<a href={urlToDownloadExamplePayouts} download>
									aquí
								</a>{' '}
								la plantilla y rellena la información de los pagos:
								<List>
									<ListItem>
										<ListItemStrong>Monto:</ListItemStrong> el importe del pago (solo números),
									</ListItem>
									<ListItem>
										<ListItemStrong>CLABE interbancaria</ListItemStrong> del beneficiario (18 dígitos),
									</ListItem>
									<ListItem>
										<ListItemStrong>Nombre del beneficiario</ListItemStrong>,
									</ListItem>
									<ListItem>
										<ListItemStrong>Concepto:</ListItemStrong> detalla el pago para identificarlo,
									</ListItem>
									<ListItem>
										<ListItemStrong>Referencia:</ListItemStrong> para identificar el pago (solo números),
									</ListItem>
									<ListItem>
										<ListItemStrong>Correo electrónico</ListItemStrong> del beneficiario (opcional)
									</ListItem>
								</List>
								<span>
									No edites los nombres de los encabezados de las columnas; esto hará que el archivo
									sea ilegible. Cuando hayas rellenado la plantilla, guárdala en formato .CSV y sube
									el archivo.
								</span>
							</>
						)}
						{steps === 2 &&
							'Las operaciones realizadas en días no laborales fin de semana o feriados bancarios se procesarán hasta el siguiente día hábil.'}
						{steps === 3 &&
							'Enviamos un código de seguridad al correo electrónico asociado a tu cuenta, ingrésalo para autorizar la operación.'}
						{steps === 4 &&
							'Los pagos de salida están en proceso de autorización bancaria, consulta su estatus en la sección “Salidas”.'}
						{steps === 5 &&
							'Detectamos un error en la información de los pagos de salida, descarga el archivo y corrige los errores.'}
					</ModalSubtitle>

					{steps === 1 && (
						<>
							<DragAndDropContainer>
								<DragAndDrop
									id="upload-batch-payments"
									accept={['.csv']}
									maxSize={2100000}
									onChange={handleChange}
								/>
							</DragAndDropContainer>
							{!state.buttonDisabled && (
								<Button
									onClick={() => handleContinueAction()}
									outboundColor
									disabled={handleVerifyDisabledButton()}
								>
									Siguiente
								</Button>
							)}
						</>
					)}

					{steps === 2 && (
						<>
							<OutboundResume transferResume={resumeData} />
							<ButtonContainer>
								<Button
									onClick={() => {
										setState({ ...state, buttonDisabled: true });
										setSteps(1);
									}}
								>
									Regresar
								</Button>
								<Button
									outboundColor
									onClick={() => {
										setSteps(3);
										handleGetCode();
									}}
								>
									Siguiente
								</Button>
							</ButtonContainer>
						</>
					)}

					{steps === 3 && (
						<SendCode
							value={state.code}
							name="code"
							handleChangeInput={handleChangeInput}
							handleContinueSendCode={() => handleContinueSendCode()}
							handleContinue={handleGetCode}
							handleVerifyDisabledButton={handleVerifyDisabledButton}
						/>
					)}
					{steps === 4 && <OutboundResume transferResume={processData} />}
					{steps === 5 && <OutboundResume transferResume={errorData} />}
					{steps === 5 && (
						<ButtonContainer>
							<Button
								onClick={() => {
									setState({ ...state, buttonDisabled: true });
									setSteps(1);
								}}
							>
								Regresar
							</Button>
							<DownloadLink href={error.csv} download="outbound-error.csv">
								<DownloadIcon />
								Descargar .csv
							</DownloadLink>
						</ButtonContainer>
					)}
				</ModalContainer>
			)}
		</Modal>
	);
};

export { NewBatchPayment };
